import dayjs from 'dayjs';
import {
    AiAgentChartData,
    AiAgentCustomTableData,
    AiAgentPdfExport,
    AiAgentStructuredTableData,
    AiAgentTableData,
} from 'src/types';
import {CHART_METRICS} from '../components/ChatChart';
import {columnsByType, titleLabelByType} from '../components/ChatTable';
import {formatCell} from './formatCell';
import {PdfImageSection, PdfSection, PdfTableSection} from './pdfTypes';

// Minimal shape the collector needs from a chat message — role + optional visuals.
export interface ExportMessage {
    role: 'user' | 'assistant';
    content: string;
    charts?: AiAgentChartData[];
    tables?: AiAgentTableData[];
}

type FormatMessage = (descriptor: {id: string; defaultMessage?: string}) => string;
interface ChartImg {dataUrl: string; width: number; height: number}
// Per-metric chart images from the export rig, keyed "{chartIdx}:{metric}" (all four
// metric views). `SingleChartImages` is the fallback: the single on-screen chart image
// (whatever metric was selected), keyed by chart index.
export type PerMetricImages = Map<string, ChartImg>;
export type SingleChartImages = Map<number, ChartImg>;

// Turn the agent's chat narration (light markdown) into clean prose for a caption.
// Also drops raw markdown-table lines and horizontal rules the agent may have left in
// its narration — those render as ugly `| a | b |` / `---` artifacts in the PDF.
const stripMarkdown = (s: string): string =>
    s
        .replace(/```[\s\S]*?```/g, '')            // fenced code
        .replace(/`([^`]+)`/g, '$1')               // inline code
        .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1') // links / images → text
        .replace(/^\s{0,3}\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/gm, '') // md table separator row
        .replace(/^\s{0,3}\|.*\|\s*$/gm, '')       // md table body/header rows
        .replace(/^\s{0,3}-{3,}\s*$/gm, '')        // horizontal rule ---
        .replace(/^\s{0,3}#{1,6}\s+/gm, '')        // headings
        .replace(/^\s{0,3}[-*+]\s+/gm, '• ')       // bullets
        .replace(/(\*\*|__)(.*?)\1/g, '$2')        // bold
        .replace(/(\*|_)(.*?)\1/g, '$2')           // italic
        .replace(/\r/g, '')
        .replace(/[ \t]+\n/g, '\n')                // trailing spaces
        .replace(/\n{3,}/g, '\n\n')
        .trim();

// A table/chart's PDF description is the agent's narration from that turn — which is
// often its "thinking out loud" plan BEFORE calling tools ("Good. Now let me pull the
// breakdowns … in parallel"). That's live-progress chatter, useful in the chat but NOT
// in a report. Drop such planning/filler sentences; keep genuinely descriptive prose.
// Conservative + EN-first (with a few FR/DE markers) so real descriptions survive.
const PLANNING_RE = /\b(let me|let'?s|i'?ll|i will|i'?m going to|i am going to|in parallel|now,? (?:i|let)|next,? (?:i|let)|je vais|laisse[- ]moi|en parallèle|ich werde|lass mich|parallel dazu)\b/i;
const FILLER_RE = /^(good|ok|okay|alright|all right|perfect|great|sure|got it|done|nice|excellent|parfait|bien|gut|okay)\b[\s,.!:;—-]*$/i;

const stripPlanning = (text: string): string => {
    if (!text) return '';
    // Split into sentences (ender+space → newline, then split lines) — no lookbehind
    // for broad browser support.
    const sentences = text.replace(/([.!?])\s+/g, '$1\n').split(/\n+/);
    return sentences
        .map((s) => s.trim())
        .filter((s) => s && !FILLER_RE.test(s) && !PLANNING_RE.test(s))
        .join(' ')
        .trim();
};

// --- Tables ------------------------------------------------------------------

// No row cap: the PDF shows every row the chat table has. Tables flow after the text
// and wrap across pages, so any length paginates cleanly. (Chat tables are themselves
// bounded by the backend's renderTable 50-row limit.)

const customTableToSection = (t: AiAgentCustomTableData): PdfTableSection => ({
    kind: 'table',
    title: t.title,
    headers: t.columns.map((c) => c.label),
    aligns: t.columns.map((c) => c.align ?? (c.format === 'text' || c.format === 'date' ? 'left' : 'right')),
    rows: t.rows.map((row) => t.columns.map((c) => formatCell(row[c.key], c.format))),
    footer: t.footer || undefined,
});

const structuredTableToSection = (t: AiAgentStructuredTableData, fm: FormatMessage): PdfTableSection => {
    const cols = columnsByType[t.type] ?? [];
    return {
        kind: 'table',
        title: fm({id: titleLabelByType[t.type]}),
        headers: cols.map((c) => fm({id: c.labelId})),
        rows: t.rows.map((row) => cols.map((c) => c.textValue(row) || '—')),
    };
};

const tableToSection = (t: AiAgentTableData, fm: FormatMessage): PdfTableSection =>
    t.type === 'custom' ? customTableToSection(t) : structuredTableToSection(t, fm);

// --- Charts ------------------------------------------------------------------

const chartTitle = (chart: AiAgentChartData, fm: FormatMessage): string =>
    chart.title || fm({id: 'consumption', defaultMessage: 'Consumption'});

// A one-line data summary shown beneath a chart image: period + totals.
const chartCaption = (chart: AiAgentChartData, fm: FormatMessage): string => {
    const pts = chart.points ?? [];
    if (pts.length === 0) return '';
    const dateFmt = chart.granularity === 'HOURLY' ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY';
    const from = dayjs(pts[0].timestamp).format(dateFmt);
    const to = dayjs(pts[pts.length - 1].timestamp).format(dateFmt);
    const kwh = pts.reduce((a, p) => a + (p.consumption ?? 0), 0) / 1000;
    const cost = pts.reduce((a, p) => a + (p.cost ?? 0), 0) / 10000;
    const sessions = pts.reduce((a, p) => a + (p.transactionCount ?? 0), 0);
    const anomalies = pts.reduce((a, p) => a + (p.anomaly ? 1 : 0), 0);
    const parts = [
        `${from} – ${to}`,
        `${fm({id: 'energy', defaultMessage: 'Energy'})}: ${kwh.toFixed(1)} kWh`,
        `${fm({id: 'cost', defaultMessage: 'Cost'})}: CHF ${cost.toFixed(2)}`,
        `${fm({id: 'sessions', defaultMessage: 'Sessions'})}: ${sessions}`,
    ];
    if (anomalies > 0) parts.push(`${fm({id: 'ecAnomaly', defaultMessage: 'Anomaly'})}: ${anomalies}`);
    return parts.join('   ·   ');
};

// Phase 1 fallback when SVG capture is unavailable: the chart's underlying data table.
const chartToTableSection = (chart: AiAgentChartData, fm: FormatMessage): PdfTableSection => {
    const dateFmt = chart.granularity === 'HOURLY' ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY';
    return {
        kind: 'table',
        title: chartTitle(chart, fm),
        headers: [
            fm({id: 'period', defaultMessage: 'Period'}),
            fm({id: 'sessions', defaultMessage: 'Sessions'}),
            fm({id: 'energy', defaultMessage: 'Energy'}),
            fm({id: 'cost', defaultMessage: 'Cost'}),
        ],
        aligns: ['left', 'right', 'right', 'right'],
        rows: (chart.points ?? []).map((p) => [
            dayjs(p.timestamp).format(dateFmt),
            String(p.transactionCount ?? 0),
            `${((p.consumption ?? 0) / 1000).toFixed(1)} kWh`,
            `CHF ${((p.cost ?? 0) / 10000).toFixed(2)}`,
        ]),
    };
};

// Single on-screen chart image (fallback when the per-metric rig capture is absent).
const chartToImageSection = (chart: AiAgentChartData, fm: FormatMessage, img: ChartImg): PdfImageSection => ({
    kind: 'image',
    title: chartTitle(chart, fm),
    caption: chartCaption(chart, fm),
    dataUrl: img.dataUrl,
    width: img.width,
    height: img.height,
});

const chartPeriod = (chart: AiAgentChartData): string => {
    const pts = chart.points ?? [];
    if (pts.length === 0) return '';
    const dateFmt = chart.granularity === 'HOURLY' ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY';
    return `${dayjs(pts[0].timestamp).format(dateFmt)} – ${dayjs(pts[pts.length - 1].timestamp).format(dateFmt)}`;
};

// One image section per captured metric view (Energy / Cost / Sessions / Avg price),
// titled "<chart title> — <metric>". Only metrics whose image was captured are included.
const chartMetricImageSections = (
    chart: AiAgentChartData,
    fm: FormatMessage,
    chartIdx: number,
    perMetric: PerMetricImages,
): PdfImageSection[] => {
    // Prefix with the chart's own title if it has one, else a generic "Charts" — NOT
    // the "Consumption" fallback, which produced the redundant "Consumption — Consumption".
    const base = chart.title?.trim() || fm({id: 'aiAgentPdfChartsHeading', defaultMessage: 'Charts'});
    const period = chartPeriod(chart);
    const out: PdfImageSection[] = [];
    for (const m of CHART_METRICS) {
        const img = perMetric.get(`${chartIdx}:${m.key}`);
        if (!img) continue;
        out.push({
            kind: 'image',
            title: `${base} — ${fm({id: m.labelId})}`,
            caption: period,
            dataUrl: img.dataUrl,
            width: img.width,
            height: img.height,
        });
    }
    return out;
};

// --- Visual entries (ordered, carry their message narration) ------------------

interface VisualEntry {
    msgIndex: number;
    narration: string;
    kind: 'table' | 'chart';
    // Returns ONE OR MORE sections (a chart may expand into 4 per-metric images).
    build: () => (PdfTableSection | PdfImageSection)[];
}

// Content signatures for de-duplication: if the user asked the same thing twice, the
// same table/chart appears in several messages — keep only the first. Key on the DATA
// ONLY (rows / points), NOT the title: the agent often titles the same table
// differently across asks ("Invoices" vs "Recent invoices"), so a title-sensitive key
// would let visually-identical tables through. Row objects are normalised (sorted
// keys) so a different key order in the JSON doesn't defeat the match.
const normRows = (rows: Array<Record<string, unknown>>): string =>
    JSON.stringify(rows.map((r) => Object.keys(r).sort().map((k) => [k, r[k]])));

const tableSig = (t: AiAgentTableData): string =>
    t.type === 'custom'
        ? `t|${t.columns.map((c) => c.key).sort().join(',')}|${normRows(t.rows)}`
        : `t|${t.type}|${normRows(t.rows)}`;

const chartSig = (chart: AiAgentChartData): string => `c|${JSON.stringify(chart.points ?? [])}`;

// Flatten every table/chart across the chat into an ordered list, each entry
// carrying the narration of the message that introduced it and knowing how to
// render itself. DUPLICATES are dropped (same data twice → shown once). Charts are
// numbered in document order to line up with `captureCharts()` (messages top-to-
// bottom, charts in array order) — so the index advances even for skipped duplicates.
const visualEntries = (
    messages: ExportMessage[],
    fm: FormatMessage,
    perMetric: PerMetricImages | undefined,
    singleImages: SingleChartImages | undefined,
    captions: Map<string, string>,
    chartInsight: string | undefined,
): VisualEntry[] => {
    const entries: VisualEntry[] = [];
    const seen = new Set<string>();
    let chartIdx = 0;
    messages.forEach((m, msgIndex) => {
        if (m.role !== 'assistant') return;
        // Fallback description = the turn's narration, minus the agent's planning/filler
        // chatter. The agent's authored caption (below) takes priority over this.
        const narration = stripPlanning(stripMarkdown(m.content || ''));
        (m.charts ?? []).forEach((chart) => {
            const idx = chartIdx++;          // advance for EVERY chart (DOM order)
            const sig = chartSig(chart);
            if (seen.has(sig)) return;       // duplicate → skip (image index already advanced)
            seen.add(sig);
            entries.push({
                msgIndex,
                narration,
                kind: 'chart',
                build: () => {
                    // Prefer all-4 per-metric images from the rig; else the single
                    // on-screen image; else the data-table fallback.
                    const metricImgs = perMetric ? chartMetricImageSections(chart, fm, idx, perMetric) : [];
                    const sections = metricImgs.length
                        ? metricImgs
                        : singleImages?.get(idx)
                            ? [chartToImageSection(chart, fm, singleImages.get(idx) as ChartImg)]
                            : [chartToTableSection(chart, fm)];
                    // Agent's authored chart insight leads the chart block.
                    if (chartInsight && sections.length) sections[0].description = chartInsight;
                    return sections;
                },
            });
        });
        (m.tables ?? []).forEach((table) => {
            const sig = tableSig(table);
            if (seen.has(sig)) return;       // duplicate table → skip
            seen.add(sig);
            entries.push({
                msgIndex,
                narration,
                kind: 'table',
                build: () => {
                    const section = tableToSection(table, fm);
                    // Agent's authored caption (matched by the table's title) wins.
                    const cap = captions.get((section.title ?? '').trim().toLowerCase());
                    if (cap) section.description = cap;
                    return [section];
                },
            });
        });
    });
    return entries;
};

// Render a subset of visual entries, attaching each message's narration as the
// `description` of the FIRST section built from that message (so a description
// isn't repeated across a message's several tables/charts).
const renderEntries = (entries: VisualEntry[]): PdfSection[] => {
    const describedMsgs = new Set<number>();
    const out: PdfSection[] = [];
    for (const e of entries) {
        const sections = e.build();
        // Fall back to the message narration ONLY when the agent didn't author a
        // caption for this section, and only once per message (avoid repetition).
        if (sections.length && !sections[0].description && e.narration && !describedMsgs.has(e.msgIndex)) {
            describedMsgs.add(e.msgIndex);
            sections[0].description = e.narration;
        }
        out.push(...sections);
    }
    return out;
};

/**
 * Resolve the sections a PDF should contain from the chat state, per the agent's
 * export descriptor (`scope`). Pure data-in/data-out (no DOM) — react-pdf rendering
 * happens in `buildPdfBlob`.
 *
 * The agent's chat narration becomes each table/chart's description, so the report
 * reads with context instead of being a bare screenshot. `perMetricImages` holds all
 * four metric views of each chart (from the export rig); `singleImages` is the on-screen
 * fallback (selected metric). Both absent for a chart → its data-table fallback.
 */
export const collectExportContent = (
    exp: AiAgentPdfExport,
    messages: ExportMessage[],
    fm: FormatMessage,
    perMetricImages?: PerMetricImages,
    singleImages?: SingleChartImages,
): PdfSection[] => {
    // Agent-authored report copy (from the exportPdf call): per-table captions (keyed
    // by title) + a chart insight. These take priority over copied chat narration.
    const captions = new Map<string, string>();
    for (const c of exp.captions ?? []) {
        if (c.title && c.text) captions.set(c.title.trim().toLowerCase(), c.text);
    }
    const chartInsight = exp.chartInsight?.trim() || undefined;
    const entries = visualEntries(messages, fm, perMetricImages, singleImages, captions, chartInsight);
    // Opening block heading — an executive OVERVIEW that leads the report (not a
    // closing "Summary"); followed by the deduplicated tables/charts.
    const overviewTitle = fm({id: 'aiAgentPdfSummaryHeading', defaultMessage: 'Overview'});

    switch (exp.scope) {
        case 'last_table': {
            const tables = entries.filter((e) => e.kind === 'table');
            const last = tables[tables.length - 1];
            return last ? renderEntries([last]) : [];
        }
        case 'all_tables':
            return renderEntries(entries.filter((e) => e.kind === 'table'));
        case 'charts':
            return renderEntries(entries.filter((e) => e.kind === 'chart'));
        case 'summary':
            return exp.summary ? [{kind: 'text', title: overviewTitle, body: stripMarkdown(exp.summary)}] : [];
        case 'everything': {
            const sections: PdfSection[] = [];
            const intro = (exp.summary || '').trim();
            if (intro) sections.push({kind: 'text', title: overviewTitle, body: stripMarkdown(intro)});
            sections.push(...renderEntries(entries));
            return sections;
        }
        default:
            return [];
    }
};
